import classNames from 'classnames';
import { FC } from 'react';
import { ButtonVariants } from '../../../components/Button/Button.types';
import ButtonLink from '../../../components/ButtonLink/ButtonLink';
import Header from '../../../components/Header/Header';
import { useActiveGlobalFreezes } from '../../../services/freeze/freeze.service';
import { useCurrentUser } from '../../../services/user/user.service';
import useFranchiseeAlias from '../../../utils/useFranchiseeAlias/useFranchiseeAlias';
import styles from './MyProfile.module.css';
import { useTranslation } from 'react-i18next';
import { User } from '@/types/auth.types';
import { GlobalFreeze } from '@/types/freeze.types';

export function freezeExistsInData(activeGlobalFreezeData?: { data: GlobalFreeze[] }) {
  return activeGlobalFreezeData?.data && activeGlobalFreezeData?.data?.length > 0;
}

export function getPersonalName(user: User | undefined) {
  return user?.firstName || user?.lastName ? `${user?.firstName || ''} ${user?.lastName || ''}` : '-';
}

const MyProfile: FC = () => {
  const { t } = useTranslation();
  const { data } = useCurrentUser(true);
  const user = data?.data;
  const { alias } = useFranchiseeAlias();
  const { data: activeGlobalFreezeData } = useActiveGlobalFreezes(true);
  const freezeExists = freezeExistsInData(activeGlobalFreezeData);
  const personalName = getPersonalName(user);

  return (
    <>
      <Header title={t('myProfile.title')} breadcrumb={{ to: '/', label: t('myProfile.breadcrumb') }} includeLogOut />
      <div
        className={classNames(styles.page, { [styles.withAlias]: alias !== null, [styles.withFreeze]: freezeExists })}
      >
        <div className={styles.profileSection}>
          <div className={styles.infoColumn}>
            <h2>{t('myProfile.myInformationHeader')}</h2>
            <div>
              <span className={styles.bold}>{t('myProfile.informationNameLabel')}:</span>
              {personalName}
            </div>
            <div>
              <span className={styles.bold}>{t('myProfile.informationEmailLabel')}:</span> {user?.email || '-'}
            </div>
            <div className={styles.button}>
              <ButtonLink variant={ButtonVariants.Secondary} to="/account/stores">
                {t('myProfile.viewMyStoresButton')}
              </ButtonLink>
            </div>
          </div>
          <div className={styles.divider} />
          <div className={styles.infoColumn}>
            <h2>{t('myProfile.myOrganizationHeader')}</h2>
            <div>
              <span className={styles.bold}>{t('myProfile.organizationIdLabel')}:</span> {user?.orgId || '-'}
            </div>
            <div>
              <span className={styles.bold}>{t('myProfile.fzCodeLabel')}:</span> {user?.fzCode || '-'}
            </div>
            <div>
              <span className={styles.bold}>{t('myProfile.organizationUsersLabel')}:</span> {user?.orgUserCount || 0}
            </div>
            <div className={styles.button}>
              <ButtonLink variant={ButtonVariants.Secondary} to="/account/org" type="button">
                {t('myProfile.viewMyOrganizationButton')}
              </ButtonLink>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MyProfile;
